// TODO(PE-1215 follow-up): split this shared component into
// `visit-list-item/kinsa.tsx` and `visit-list-item/owlet.tsx` — two
// separate implementations, each hardcoding its own brand's behavior —
// and pick between them via `resolveAlias` (see apps/platform/next.config.ts,
// per the "Brand-specific Code" recipe in CLAUDE.md). The current
// config-prop pattern was a stepping stone to delete the in-file brand
// ternaries; the next move is to delete the prop seam entirely.
import { ExternalMemberID, ProviderNetworkMemberID } from "@repo/db";
import { asMDICaseID, asMDIPatientID } from "@repo/db/branded-types";
import {
  Case,
  CASE_STATUS,
  mapSDKStatusToCaseStatus,
  UnreadMessageInfo,
} from "@repo/provider-network";
import RedXIcon from "@repo/ui/resources/red-x-icon.svg";
import TealCheckIcon from "@repo/ui/resources/teal-check-icon.svg";
import TealClockIcon from "@repo/ui/resources/teal-clock-icon.svg";
import Image from "next/image";
import Link from "next/link";
import MessageIcon from "public/fas-fa-comments.svg";
import type { ReactNode } from "react";
import { AvatarGroup } from "@repo/ui/avatar";
import {
  BodyText,
  BodyTitleText,
  SublineCondensedText,
} from "src/components/KinsaTypography";
import { formatClinicianName } from "src/lib/clinician-name";
import { KinsaInitialContext } from "src/lib/definitions";
import { getVisitUrl } from "src/lib/routes";
import {
  getAvatarFallback,
  getPatientAvatar,
} from "src/lib/utils/avatar-utils";
import { twMerge } from "tailwind-merge";
import {
  defaultPastVisitConfig,
  type PastVisitCardConfig,
} from "./past-visit-item";

type PatientMapping = Array<{
  providerNetworkMemberId?: ProviderNetworkMemberID | null;
  externalMemberId?: ExternalMemberID | null;
}>;

/** Brand-specific function that resolves a visit to its destination URL. */
export type VisitHrefResolver = (
  visit: Case,
  patientMapping?: PatientMapping
) => string | undefined;

// Default (Kinsa) chevron rendered on vertical (active/requested) cards.
const kinsaVerticalChevron: ReactNode = (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="select-none"
    onContextMenu={(e) => e.preventDefault()}
  >
    <g clipPath="url(#clip0_7119_2271_active)">
      <path
        d="M9.53027 5.47012C9.82324 5.76309 9.82324 6.23887 9.53027 6.53184L5.03027 11.0318C4.7373 11.3248 4.26152 11.3248 3.96855 11.0318C3.67559 10.7389 3.67559 10.2631 3.96855 9.97012L7.93887 5.9998L3.9709 2.02949C3.67793 1.73652 3.67793 1.26074 3.9709 0.967773C4.26387 0.674805 4.73965 0.674805 5.03262 0.967773L9.53262 5.46777L9.53027 5.47012Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_7119_2271_active">
        <rect
          width="7.5"
          height="12"
          fill="white"
          transform="matrix(-1 0 0 1 9.75 0)"
        />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Brand-specific visual/behavioral config for visit cards.
 *
 * Extends `PastVisitCardConfig` so a single per-brand config drives both
 * `VisitListItem` (active/requested/completed-inline) and `PastVisitItem`
 * (history list). Callers thread one `brandConfig` down the tree; TS lets us
 * hand the same value to `PastVisitItem` because of the extends relationship.
 *
 * `requestedSubtitle` and `showRequestedSmartBar` are an intentional XOR:
 * a Requested visit either renders the subtitle in-card (Owlet) or shows
 * the smart status bar beneath it (Kinsa), never both.
 */
export interface VisitCardBrandConfig extends PastVisitCardConfig {
  /** Show clock icon next to "Requested" status label */
  showRequestedClock: boolean;
  /** In-card subtitle for Requested visits (null = use smart bar instead) */
  requestedSubtitle: string | null;
  /** Show smart status bar below Requested visits (XOR with `requestedSubtitle`) */
  showRequestedSmartBar: boolean;
  /** Show smart status bar below Active+unread visits */
  showActiveUnreadSmartBar: boolean;
  /** Use letter initial for patient avatar (vs generated image) */
  useInitialAvatar: boolean;
  /** Color context applied to the vertical chevron wrapper (for `currentColor` SVGs) */
  verticalChevronClass: string;
  /** Completed date component: "body" = BodyText, "subline" = SublineCondensedText */
  completedDateStyle: "body" | "subline";
  /** Smart status style for active+unread: "masked-icon" or "icon-text" */
  unreadSmartBarStyle: "masked-icon" | "icon-text";
  /** Smart status bg class for active+unread */
  unreadBgClass: string;
  /** Chevron SVG node for vertical (active/requested) cards */
  verticalChevron: ReactNode;
  /**
   * Render past visits inline on the home under a "History" heading (Kinsa),
   * vs. hiding them from the home entirely and surfacing them only via the
   * "See past visits" link → dedicated `/past-visits` route (Owlet, PE-1374).
   */
  inlinePastVisits: boolean;
}

export const defaultBrandConfig: VisitCardBrandConfig = {
  ...defaultPastVisitConfig,
  showRequestedClock: true,
  requestedSubtitle: null,
  showRequestedSmartBar: true,
  showActiveUnreadSmartBar: false,
  useInitialAvatar: false,
  verticalChevronClass: "text-[var(--color-teal-600)]",
  completedDateStyle: "subline",
  unreadSmartBarStyle: "icon-text",
  unreadBgClass: "bg-primary/20",
  verticalChevron: kinsaVerticalChevron,
  inlinePastVisits: true,
};

interface VisitListItemProps {
  visit: Case;
  className?: string;
  unreadInfo?: UnreadMessageInfo;
  coveredPersons?: KinsaInitialContext["account"]["coveredPersons"];
  patientMapping?: PatientMapping;
  /** Brand-specific visit href resolver. Falls back to default (Active/Completed → /visit/). */
  getVisitHref?: VisitHrefResolver;
  /** Brand-specific visual/behavioral config. Falls back to Kinsa defaults. */
  brandConfig?: VisitCardBrandConfig;
}

interface StatusDisplayProps {
  status: string;
  showRequestedClock: boolean;
}

/**
 * Maps visit status to display labels and icons
 */
const getStatusDisplay = (status: string) => {
  switch (status) {
    case CASE_STATUS.REQUESTED:
      return {
        text: "Requested",
        iconPath: "/icons/ClockIcon.svg",
        className: "text-orange-600",
      };
    case CASE_STATUS.ACTIVE:
      return {
        text: "Active",
        iconPath: null,
        className: "text-blue-600",
      };
    case CASE_STATUS.COMPLETED:
      return {
        text: "Completed",
        iconPath: "/icons/CheckIcon.svg",
        className: "text-gray-600",
      };
    case CASE_STATUS.CANCELLED:
      return {
        text: "Canceled Visit",
        iconPath: "/icons/CancelIcon.svg",
        className: "text-gray-600",
      };
    default:
      return {
        text: "Unknown",
        iconPath: "/icons/ClockIcon.svg",
        className: "text-gray-600",
      };
  }
};

const StatusDisplay: React.FC<StatusDisplayProps> = ({
  status,
  showRequestedClock: showClock,
}) => {
  const statusInfo = getStatusDisplay(status);
  const isCancelled = status === CASE_STATUS.CANCELLED;
  const isRequested = status === CASE_STATUS.REQUESTED;

  return (
    <div className="flex items-center gap-2">
      <BodyText className="!text-foreground text-xs font-semibold leading-4">
        <span className="inline-flex items-center gap-1">
          {isRequested && showClock && (
            <Image
              src={TealClockIcon}
              alt="Requested"
              width={12}
              height={12}
              className="select-none"
              draggable={false}
              unoptimized
            />
          )}
          {isCancelled && (
            <Image
              alt="Canceled"
              src={RedXIcon}
              width={12}
              height={12}
              className="select-none"
              draggable={false}
              unoptimized
            />
          )}
          {statusInfo.text}
        </span>
      </BodyText>
    </div>
  );
};

/**
 * Formats timestamp for display based on visit status
 * - Active visits: show timestamp only (not rolling counter) in lowercase with periods
 * - Past visits: show date only (no timestamps)
 * - Requested visits: show timestamp only in lowercase with periods
 */
const formatTimestamp = (timestamp: string, status: string): string => {
  const date = new Date(timestamp);

  // For active visits, show timestamp only
  if (status === CASE_STATUS.ACTIVE || status === CASE_STATUS.REQUESTED) {
    return date
      .toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      .toLowerCase()
      .replace(/am/g, "a.m.")
      .replace(/pm/g, "p.m.");
  }

  // For past visits (completed, cancelled), show short date (M/D/YY)
  return date.toLocaleDateString("en-US", {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
  });
};

/**
 * Gets the appropriate avatar state based on visit status
 */
const getAvatarState = (status: string, hasClinician: boolean) => {
  // For requested or visits without clinician, show not-assigned (animated) state
  // For cancelled visits, always show assigned state (no animation)
  if (
    status === CASE_STATUS.REQUESTED ||
    (!hasClinician && status !== CASE_STATUS.CANCELLED)
  ) {
    return "not-assigned";
  }
  return "assigned";
};

/**
 * Default visit href resolver (Kinsa): Active and Completed → /visit/.
 */
const defaultGetVisitHref: VisitHrefResolver = (visit) => {
  const status = mapSDKStatusToCaseStatus(visit.case_status?.name);
  const caseId = visit.case_id || visit.id || "";
  const patientId = visit.patient?.patient_id || "";

  switch (status) {
    case CASE_STATUS.ACTIVE:
    case CASE_STATUS.COMPLETED:
      return getVisitUrl(asMDIPatientID(patientId), asMDICaseID(caseId));
    default:
      return undefined;
  }
};

/**
 * Gets the visit title based on status and patient info
 */
const getVisitTitle = (visit: Case): string => {
  const patientFirstName = visit.patient?.first_name || "";
  const doctorName = formatClinicianName(visit.case_assignment?.clinician);
  const status = mapSDKStatusToCaseStatus(visit.case_status?.name);

  switch (status) {
    case CASE_STATUS.REQUESTED:
      return patientFirstName
        ? `Reviewing ${patientFirstName}'s Details`
        : "Reviewing Patient Details";
    case CASE_STATUS.ACTIVE: {
      if (patientFirstName && doctorName) {
        return `${patientFirstName} & ${doctorName}`;
      }
      return patientFirstName ? `${patientFirstName}'s Visit` : "Patient Visit";
    }
    case CASE_STATUS.COMPLETED: {
      if (patientFirstName && doctorName) {
        return `${patientFirstName} & ${doctorName}`;
      }
      return patientFirstName || "Visit Summary";
    }
    case CASE_STATUS.CANCELLED: {
      const patientFullName = visit.patient
        ? `${visit.patient.first_name || ""} ${visit.patient.last_name || ""}`.trim()
        : "";
      return patientFullName || "Canceled Visit";
    }
    default: {
      const defaultPatientName = visit.patient
        ? `${visit.patient.first_name || ""} ${visit.patient.last_name || ""}`.trim()
        : "";
      return defaultPatientName
        ? `${defaultPatientName}'s Visit`
        : "Patient Visit";
    }
  }
};

/**
 * Gets the visit subtitle based on status
 */
const getVisitSubtitle = (visit: Case): string | React.ReactElement => {
  const status = mapSDKStatusToCaseStatus(visit.case_status?.name);

  switch (status) {
    case CASE_STATUS.REQUESTED:
      return "Request details";
    case CASE_STATUS.ACTIVE:
      return "Your visit details";
    case CASE_STATUS.COMPLETED:
      return "Visit Summary";
    case CASE_STATUS.CANCELLED:
      return (
        <span className="flex items-center gap-2">
          Canceled Visit
          <Image
            src={RedXIcon}
            alt="Canceled"
            width={16}
            height={16}
            className="select-none"
            draggable={false}
            unoptimized
          />
        </span>
      );
    default:
      return "No recent activity";
  }
};

const VisitListItem: React.FC<VisitListItemProps> = ({
  visit,
  className,
  unreadInfo,
  coveredPersons,
  patientMapping,
  getVisitHref = defaultGetVisitHref,
  brandConfig = defaultBrandConfig,
}) => {
  // Map the SDK status to our standardized status
  const status = mapSDKStatusToCaseStatus(visit.case_status?.name);
  const href = getVisitHref(visit, patientMapping);
  const isClickable = !!href;
  const hasRequestedSubtitle =
    status === CASE_STATUS.REQUESTED && !!brandConfig.requestedSubtitle;
  const isActiveUnread =
    status === CASE_STATUS.ACTIVE && !!unreadInfo?.hasUnread;
  const showInCardChevron = isClickable || hasRequestedSubtitle;

  // Get patient avatar by matching visit patient with coveredPersons
  let patientAvatar: ReturnType<typeof getPatientAvatar>;

  if (coveredPersons && visit.patient) {
    let matchingPatient = null;

    // First try ID-based matching using patientMapping
    const providerPatientId = visit.patient?.patient_id;
    if (patientMapping && providerPatientId) {
      const mappingEntry = patientMapping.find(
        (p) => p.providerNetworkMemberId === providerPatientId
      );

      if (mappingEntry?.externalMemberId) {
        matchingPatient = coveredPersons.find(
          (person) => person.id === mappingEntry.externalMemberId
        );
      }
    }

    // Fallback to name-based matching if ID matching didn't work.
    // Owlet's covered-person shape only carries firstName — guard each field
    // so a missing lastName doesn't crash the render. Skip the match entirely
    // if either side is missing the compared field.
    if (!matchingPatient) {
      const visitFirst = visit.patient?.first_name?.toLowerCase();
      const visitLast = visit.patient?.last_name?.toLowerCase();
      matchingPatient = coveredPersons.find((person) => {
        const personFirst = person.firstName?.toLowerCase();
        const personLast = (
          person as { lastName?: string }
        ).lastName?.toLowerCase();
        if (!personFirst || !visitFirst) return false;
        if (personFirst !== visitFirst) return false;
        // If either side lacks a lastName, accept a firstName-only match.
        if (!personLast || !visitLast) return true;
        return personLast === visitLast;
      });
    }

    if (matchingPatient) {
      // Use the matched patient's ID to get the correct avatar
      patientAvatar = getPatientAvatar(matchingPatient.id, coveredPersons);
    } else {
      // Fallback to deterministic generation using patient_id
      patientAvatar = getAvatarFallback(visit.patient.patient_id || "");
    }
  } else {
    // Fallback to deterministic generation
    patientAvatar = getAvatarFallback(
      visit.patient?.patient_id ||
        `${visit.patient?.first_name || ""}${visit.patient?.last_name || ""}`
    );
  }

  const firstAvatarVariant = brandConfig.useInitialAvatar
    ? "initial"
    : patientAvatar;
  const firstAvatarInitial = brandConfig.useInitialAvatar
    ? visit.patient?.first_name || ""
    : undefined;

  // Card content based on Figma design patterns
  const isCompletedOrCancelled =
    status === CASE_STATUS.COMPLETED || status === CASE_STATUS.CANCELLED;
  const cardContent = isCompletedOrCancelled ? (
    // HORIZONTAL LAYOUT: Completed/Cancelled (matches Figma)
    <div className="flex gap-3 items-center w-full">
      {/* Avatar Group */}
      <div
        className={`flex-shrink-0 ${
          brandConfig.dimCancelledAvatars && status === CASE_STATUS.CANCELLED
            ? "opacity-50"
            : ""
        }`}
      >
        <AvatarGroup
          firstAvatar={firstAvatarVariant}
          firstAvatarInitial={firstAvatarInitial}
          secondAvatar={
            visit.case_assignment?.clinician &&
            visit.case_assignment.clinician.photo
              ? "custom"
              : "unassignedDoc"
          }
          secondAvatarUrl={
            visit.case_assignment?.clinician?.photo?.url_thumbnail ||
            visit.case_assignment?.clinician?.photo?.url
          }
          size="small"
          state={getAvatarState(status, !!visit.case_assignment?.clinician)}
          layout={brandConfig.avatarLayout}
        />
      </div>

      {/* Content Area: title + date stacked */}
      <div className="flex-1 min-w-0">
        <BodyTitleText className="font-sans font-bold text-foreground truncate">
          {getVisitTitle(visit)}
        </BodyTitleText>
        {status === CASE_STATUS.CANCELLED ? (
          <BodyText className={brandConfig.cancelledTextClass}>
            <span className="inline-flex items-center gap-1">
              <Image
                alt="Canceled"
                src={RedXIcon}
                width={12}
                height={12}
                className="select-none"
                draggable={false}
                unoptimized
              />
              {brandConfig.cancelledDateFormat === "inline-date"
                ? `Canceled on ${formatTimestamp(
                    visit.updated_at || visit.created_at || "",
                    status
                  )}`
                : "Canceled Visit"}
            </span>
          </BodyText>
        ) : brandConfig.completedDateStyle === "body" ? (
          <BodyText className="text-foreground">
            {formatTimestamp(
              visit.updated_at || visit.created_at || "",
              status
            )}
          </BodyText>
        ) : (
          <SublineCondensedText className="text-foreground/70">
            {formatTimestamp(
              visit.updated_at || visit.created_at || "",
              status
            )}
          </SublineCondensedText>
        )}
      </div>

      {/* Chevron — right, vertically centered */}
      {isClickable && (
        <div className="flex items-center justify-center flex-shrink-0 text-foreground">
          {brandConfig.horizontalChevron}
        </div>
      )}
    </div>
  ) : (
    // VERTICAL LAYOUT: Requested/Active (matches Figma)
    <div className="flex flex-col gap-4 w-full">
      {/* Top Row: Avatars and Status */}
      <div className="flex justify-between items-start w-full">
        {/* Avatar Group */}
        <div className="flex-shrink-0">
          <AvatarGroup
            firstAvatar={firstAvatarVariant}
            firstAvatarInitial={firstAvatarInitial}
            secondAvatar={
              visit.case_assignment?.clinician ? "custom" : "unassignedDoc"
            }
            secondAvatarUrl={
              visit.case_assignment?.clinician?.photo?.url_thumbnail ||
              visit.case_assignment?.clinician?.photo?.url
            }
            size="small"
            state={getAvatarState(status, !!visit.case_assignment?.clinician)}
          />
        </div>

        {/* Status and Timestamp */}
        <div className="flex flex-col items-end gap-0.5">
          <StatusDisplay
            status={status}
            showRequestedClock={brandConfig.showRequestedClock}
          />
          {status !== CASE_STATUS.ACTIVE && (
            <SublineCondensedText className="!text-foreground text-xs font-semibold leading-4">
              {formatTimestamp(
                visit.updated_at || visit.created_at || "",
                status
              )}
            </SublineCondensedText>
          )}
        </div>
      </div>

      <div className="space-y-0">
        {/* Title Row - for truncation */}
        <div className="flex gap-4 items-center w-full">
          <BodyTitleText className="font-sans font-bold text-foreground flex-1 truncate">
            {getVisitTitle(visit)}
          </BodyTitleText>

          {/* Invisible spacer for truncation - takes space like date does in completed cards */}
          {showInCardChevron && <div className="w-3 flex-shrink-0" />}
        </div>

        {/* Description Row - no gap */}
        {status === CASE_STATUS.ACTIVE && (
          <BodyText className="text-foreground">
            {getVisitSubtitle(visit)}
          </BodyText>
        )}
        {hasRequestedSubtitle && (
          <BodyText className="text-foreground">
            {brandConfig.requestedSubtitle}
          </BodyText>
        )}
      </div>

      {/* Chevron positioned 29px from bottom */}
      {showInCardChevron && (
        <div
          className={`absolute bottom-[29px] right-6 ${brandConfig.verticalChevronClass}`}
        >
          {brandConfig.verticalChevron}
        </div>
      )}
    </div>
  );

  // Standard card wrapper. Current-visit cards share dimensions across brands;
  // past-visit cards diverge (Owlet uses a tighter card on `bg-surface`).
  const baseCard = (
    <div
      className={twMerge(
        "relative flex items-start border-[0.5px] border-black/8 w-full",
        "rounded-2xl transition-all duration-200 select-none",
        isCompletedOrCancelled
          ? brandConfig.pastCardClasses
          : "p-6 min-h-[94px] bg-surface-elevated",
        isCompletedOrCancelled
          ? "shadow-[0px_2px_10px_0px_rgba(5,57,68,0.05)]"
          : "shadow-[0px_2px_10px_0px_rgba(5,57,68,0.08)]",
        isClickable && "cursor-pointer",
        className
      )}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="flex-1 w-full min-w-0">{cardContent}</div>
    </div>
  );

  // Wrap in Link if clickable, otherwise use base card directly
  const mainCard =
    isClickable && href ? (
      <Link title="visit-card" href={href} className="block w-full">
        {baseCard}
      </Link>
    ) : (
      baseCard
    );

  // Smart status content — style varies by brand config.
  const smartStatusContent =
    isActiveUnread && brandConfig.unreadSmartBarStyle === "masked-icon" ? (
      <div className="px-6 py-4 flex items-center gap-2">
        <div
          aria-hidden="true"
          className="bg-primary shrink-0"
          style={{
            width: 16,
            height: 14,
            maskImage: `url(${MessageIcon.src})`,
            WebkitMaskImage: `url(${MessageIcon.src})`,
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            maskSize: "contain",
            WebkitMaskSize: "contain",
            maskPosition: "center",
            WebkitMaskPosition: "center",
          }}
        />
        <BodyText className="text-foreground">
          {unreadInfo?.messageType === "support"
            ? "You have messages from support"
            : "You have messages from your doctor"}
        </BodyText>
      </div>
    ) : (
      <div className="px-6 pb-3 pt-3 flex items-start gap-2">
        <div className="shrink-0 mt-0.5">
          {status === CASE_STATUS.REQUESTED && (
            <Image
              alt="Notifications on"
              src={TealCheckIcon}
              width={16}
              height={14}
              className="select-none"
              draggable={false}
              unoptimized
            />
          )}
          {status === CASE_STATUS.ACTIVE && (
            <Image
              alt="Messages icon"
              src={MessageIcon}
              width={16}
              height={14}
              className="select-none"
              draggable={false}
              unoptimized
            />
          )}
        </div>
        {status === CASE_STATUS.REQUESTED && (
          <BodyText className="text-black-200 text-sm">
            You can close the app, we will notify you as soon as the doctor has
            responded
          </BodyText>
        )}
        {status === CASE_STATUS.ACTIVE && unreadInfo?.hasUnread && (
          <BodyText className="text-black-200 text-sm">
            {unreadInfo.messageType === "support"
              ? "You have messages from support"
              : "You have messages from your doctor"}
          </BodyText>
        )}
      </div>
    );

  const shouldShowSmartStatus =
    (status === CASE_STATUS.REQUESTED && brandConfig.showRequestedSmartBar) ||
    (isActiveUnread && brandConfig.showActiveUnreadSmartBar);

  return (
    <div className="w-full">
      <div
        className={`w-full rounded-2xl transition-colors duration-300 ${
          shouldShowSmartStatus
            ? isActiveUnread
              ? brandConfig.unreadBgClass
              : "bg-primary/20"
            : "bg-transparent"
        }`}
      >
        {mainCard}
        <div
          className={`transition-all duration-300 ${
            shouldShowSmartStatus
              ? "opacity-100 max-h-20"
              : "opacity-0 max-h-0 overflow-hidden"
          }`}
        >
          {smartStatusContent}
        </div>
      </div>
    </div>
  );
};

export default VisitListItem;
